import { Skeleton, Typography } from "@mui/material";
import React, { useEffect, useRef } from "react";

import { captureImageLoadFailure } from "utils/sentryErrorTracking";

interface CanvasRendererWrapperProps {
  imageStatus: "loading" | "failed" | "loaded";
  normalImageDimensions: { width: number; height: number };
  children: React.ReactElement;
  imageUrl?: string;
}

export function CanvasRendererWrapper({
  imageStatus,
  normalImageDimensions,
  children,
  imageUrl,
}: CanvasRendererWrapperProps) {

  const reportedFailureRef = useRef<string | null>(null);

  useEffect(() => {
    if (imageStatus === "failed" && imageUrl && reportedFailureRef.current !== imageUrl) {
      captureImageLoadFailure(imageUrl, "CanvasRendererWrapper");
      reportedFailureRef.current = imageUrl;
    }
  }, [imageStatus, imageUrl]);

  /* Show loader only when the canvas dimensions are not calculated. Otherwise it shows the
    loader pushed to the left if there's a delay between the calculations and the image rendering.
    This only happens sometimes but is noticeable and makes the UI feel buggy */
  if (imageStatus === "loading" && normalImageDimensions.width === 0) {
    return <Skeleton variant="rectangular" animation="wave" height="100%" width="100%" />;
  }

  if (imageStatus === "failed") {
    return (
      <div style={{ position: "relative", minHeight: "20rem", width: "100%" }}>
        <Skeleton
          variant="rectangular"
          sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, minHeight: "100%" }}
        />
        <Typography
          align="center"
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          There was a problem loading the image. Please try again.
        </Typography>
      </div>
    );
  }

  return children;
}

export default CanvasRendererWrapper;
